import type { Fn } from '@vueuse/shared'

import { LocationDataCodes, type LocationSmartResponse } from '~~/shared/@types/compliance'

type ComplianceIds = {
  deviceProfiling: ComplianceValues
  geoCheck: ComplianceValues
}

type ComplianceValues = {
  current?: Maybe<string>
  lastUpdated?: Maybe<Date>
  previous?: Maybe<string>
}

export const RESET_COMPLIANCE_IDS_TYPES = {
  All: 'all',
  DeviceProfiling: 'deviceProfiling',
  GeoCheck: 'geoCheck',
  None: 'none',
} as const

export enum ComplianceError {
  DeviceProfilingDeniedPermissions = 'compliance.error.deviceprofiling.deniedpermissions',
  DeviceProfilingGeneral = 'compliance.deviceprofiling.error.general',
  DeviceProfilingGeodataUnavailable = 'compliance.error.deviceprofiling.geodataunavailable',
  GeoCheckDeniedPermissions = 'compliance.geocheck.error.deniedpermissions',
  GeoCheckGeneral = 'compliance.geocheck.error.general',
  GeoCheckWrongLocationData = 'compliance.geocheck.error.wronglocationdata',
  IpHostingFacilityDetected = 'compliance.geocheck.error.iphostingfacilitydetected',
  LocationSpoofingDetected = 'compliance.geocheck.error.locationspoofingdetected',
}

type Interval = Partial<
  Record<'deviceProfilingInterval' | 'geoCheckInterval', Maybe<NodeJS.Timeout>>
>

type ResetComplianceIdType = ObjectValues<typeof RESET_COMPLIANCE_IDS_TYPES>

type Timeout = Partial<
  Record<
    'deviceProfilingTimeout' | 'geoCheckSuccessTimeout' | 'geoCheckTimeout',
    Maybe<NodeJS.Timeout>
  >
>

const RESET_DEVICE_COMPLIANCE = [
  RESET_COMPLIANCE_IDS_TYPES.All,
  RESET_COMPLIANCE_IDS_TYPES.DeviceProfiling,
] as string[]

const RESET_GEO_CHECK = [
  RESET_COMPLIANCE_IDS_TYPES.All,
  RESET_COMPLIANCE_IDS_TYPES.GeoCheck,
] as string[]

export const useCompliance = () => {
  const { isNativeApp } = useCustomContext()
  const { log } = useDatadog()
  const { load: loadScript } = useLoadScript()

  const cleanupVisibilityHandler = useState<Fn>()
  const error = useState((): Maybe<ComplianceError> => null)
  const ipAddress = useState((): Maybe<string> => null)
  const complianceIds = useState((): ComplianceIds => ({
    deviceProfiling: {
      current: null,
      previous: null,
    },
    geoCheck: {
      current: null,
      previous: null,
    },
  }))

  const timeouts = useState<Timeout>(() => ({}))
  const intervals = useState<Interval>(() => ({}))

  const {
    compliance: { deviceProfiling: deviceProfilingConfig, geoCheck: geoCheckConfig },
    datadog: { env },
  } = useRuntimeConfig().public

  const isDeviceProfilingRunning = useState(() => false)
  const isDeviceAppProfilingRunning = useState(() => false)
  const isGeoCheckRunning = useState(() => false)
  const isMounted = ref(false)

  const html5SessionId = computed(
    () =>
      complianceIds.value.geoCheck?.current ?? complianceIds.value.geoCheck?.previous ?? undefined,
  )

  const isComplianceLoading = computed(
    () =>
      isDeviceAppProfilingRunning.value ||
      isDeviceProfilingRunning.value ||
      isGeoCheckRunning.value,
  )

  const profilingSessionId = computed(
    () =>
      complianceIds.value.deviceProfiling?.current ??
      complianceIds.value.deviceProfiling?.previous ??
      '',
  )

  const submitterIPAddress = computed(() => ipAddress.value || undefined)

  const canCheckGeolocation = async (): Promise<boolean> => {
    if (isNativeApp.value) {
      await useFlutter()?.showLocationPermissionDialog()
      return true
    }
    if (!navigator.permissions) return false
    try {
      const { state } = await navigator.permissions.query({
        name: 'geolocation',
      })
      return state === 'granted'
    } catch {
      return false
    }
  }

  const checkComplianceIds = () => {
    const { deviceProfiling, geoCheck } = complianceIds.value
    const { intervals } = deviceProfilingConfig
    if (deviceProfiling.current && deviceProfiling.lastUpdated) {
      const diff = new Date().getTime() - deviceProfiling.lastUpdated.getTime()
      if (diff > (isNativeApp.value ? intervals.app : intervals.web)) {
        resetComplianceIds(RESET_COMPLIANCE_IDS_TYPES.DeviceProfiling)
      }
    }

    if (!isNativeApp.value && geoCheck.current && geoCheck.lastUpdated) {
      const diff = new Date().getTime() - geoCheck.lastUpdated.getTime()
      if (diff > geoCheckConfig.interval) {
        resetComplianceIds(RESET_COMPLIANCE_IDS_TYPES.GeoCheck)
      }
    }
  }

  const clearAllTimeouts = () => {
    if (!isNativeApp.value) {
      clearGeoCheckTimeout()
      clearGeoCheckSuccessTimeout()
      clearDeviceProfilingTimeout()
    }
  }

  const clearDeviceProfilingTimeout = () => {
    if (timeouts.value.deviceProfilingTimeout) {
      clearTimeout(timeouts.value.deviceProfilingTimeout)
      timeouts.value.deviceProfilingTimeout = null
    }
  }

  const clearGeoCheckTimeout = () => {
    if (timeouts.value.geoCheckTimeout) {
      clearTimeout(timeouts.value.geoCheckTimeout)
      timeouts.value.geoCheckTimeout = null
    }
  }

  const clearGeoCheckSuccessTimeout = () => {
    if (timeouts.value.geoCheckSuccessTimeout) {
      clearTimeout(timeouts.value.geoCheckSuccessTimeout)
      timeouts.value.geoCheckSuccessTimeout = null
    }
  }

  const geoCheck = () => {
    isGeoCheckRunning.value = true

    if (complianceIds.value.geoCheck.current)
      complianceIds.value.geoCheck.previous = complianceIds.value.geoCheck.current
    const sessionId = crypto.randomUUID()
    try {
      if (!timeouts.value.geoCheckTimeout) {
        timeouts.value.geoCheckTimeout = setTimeout(() => {
          setGeoCheckError()
          logComplianceError('Geocheck timed out', {
            isNativeApp: isNativeApp.value,
          })
        }, geoCheckConfig.timeouts.success)
      }

      window.lshtml5(
        sessionId,
        true,
        0,
        geoCheckConfig.timeouts.script,
        (result) => {
          if (result.statusMsg === 'Success') {
            clearGeoCheckTimeout()
            const { code, timeStamp } = result.locationData
            const parsedCode = typeof code === 'number' ? code : parseInt(code, 10)
            if (parsedCode === LocationDataCodes.Success) {
              const now = new Date().getTime()
              const locationAgeInSeconds = ((parseInt(timeStamp, 10) - now) / 1000) * -1
              if (locationAgeInSeconds < 30) {
                clearGeoCheckTimeout()
                clearGeoCheckSuccessTimeout()
                setGeoCheckResponse(result)
              } else if (!timeouts.value.geoCheckSuccessTimeout) {
                // id will be used if no updated locationData arrives after timeout
                timeouts.value.geoCheckSuccessTimeout = setTimeout(() => {
                  setGeoCheckResponse(result)
                  timeouts.value.geoCheckSuccessTimeout = null
                }, geoCheckConfig.timeouts.outdatedTimestamp)
              }

              logGeoCheckSuccess()
            }

            if (parsedCode !== LocationDataCodes.Success) {
              let errorMsg = ComplianceError.GeoCheckGeneral
              switch (parsedCode) {
                case LocationDataCodes.DeniedPermissions:
                  errorMsg = ComplianceError.GeoCheckDeniedPermissions
                  break
                case LocationDataCodes.InvalidLocationData:
                  errorMsg = ComplianceError.GeoCheckWrongLocationData
                  break
              }
              setGeoCheckError(errorMsg)
              logComplianceError(`Geo check failed with ${errorMsg}`, {
                errorMsg,
                isNativeApp: isNativeApp.value,
                parsedCode,
              })
            }
          } else {
            setGeoCheckError()
            logComplianceError('Geo check response unsuccessful', {
              isNativeApp: isNativeApp.value,
            })
          }
          isGeoCheckRunning.value = false
        },
        window.location.host,
        geoCheckConfig.watchMode,
        geoCheckConfig.watchDuration,
      )
    } catch {
      setGeoCheckError()
      isGeoCheckRunning.value = false
    }
  }

  const injectDeviceProfiling = () => {
    try {
      if (import.meta.client && !window.tmx_profiling_complete) {
        window.tmx_profiling_complete = (sessionId: string) => {
          isDeviceProfilingRunning.value = false
          clearDeviceProfilingTimeout()
          complianceIds.value.deviceProfiling.current = sessionId
          complianceIds.value.deviceProfiling.lastUpdated = new Date()
        }
      }
      if (!isNativeApp.value && !window.lsdp) {
        // insert needed scripts in browsers
        loadScript({
          options: {
            async: true,
            onload: () => profileDevice(),
          },
          src: '/scripts/toolkitV8.js',
        })
      } else {
        profileDevice()
      }
    } catch {
      setDeviceProfilingError()
    }
  }

  const injectGeoCheck = () => {
    if (!isNativeApp.value && !window.lshtml5) {
      try {
        loadScript({
          options: { async: true, onload: () => geoCheck() },
          src: useRuntimeConfig().public.compliance.geoCheck.scriptUrl,
        })
      } catch {
        setGeoCheckError()
      }
    } else {
      geoCheck()
    }
  }

  const profileDevice = () => {
    isDeviceProfilingRunning.value = true

    if (complianceIds.value.deviceProfiling.current)
      complianceIds.value.deviceProfiling.previous = complianceIds.value.deviceProfiling.current
    const sessionId = crypto.randomUUID()
    try {
      const { orgId, scriptUrl } = useRuntimeConfig().public.compliance.deviceProfiling
      if (!timeouts.value.deviceProfilingTimeout)
        timeouts.value.deviceProfilingTimeout = setTimeout(() => {
          isDeviceProfilingRunning.value = false
          resetComplianceIds(RESET_COMPLIANCE_IDS_TYPES.DeviceProfiling)
          stopCompliance()
          error.value = ComplianceError.DeviceProfilingGeneral
          logComplianceError('Device profiling timed out', {
            isNativeApp: isNativeApp.value,
          })
        }, deviceProfilingConfig.timeout)

      window.lsdp.dp(scriptUrl, orgId, sessionId)
    } catch {
      isDeviceProfilingRunning.value = false
    }
  }

  const profileDeviceApp = async () => {
    isDeviceAppProfilingRunning.value = true

    if (complianceIds.value.deviceProfiling.current)
      complianceIds.value.deviceProfiling.previous = complianceIds.value.deviceProfiling.current
    const sessionId = crypto.randomUUID()
    try {
      const result = await useFlutter()?.getLocationSmartProfile(sessionId)
      if (!result) {
        setDeviceProfilingError(ComplianceError.DeviceProfilingGeneral)
      } else {
        const { status, success } = result
        if (success) setDeviceProfilingResponse(sessionId)
        if (!success) {
          let error: ComplianceError
          switch (status) {
            case 'checkout.denied.geodata_unavailable':
              error = ComplianceError.DeviceProfilingGeodataUnavailable
              break
            case 'checkout.denied.locationpermissions':
              error = ComplianceError.DeviceProfilingDeniedPermissions
              break
            default:
              error = ComplianceError.DeviceProfilingGeneral
          }
          setDeviceProfilingError(error)
          logComplianceError(`Device profiling failed with ${error}`, {
            error,
            isNativeApp: isNativeApp.value,
            status,
          })
        }
      }
      isDeviceAppProfilingRunning.value = false
    } catch {
      setDeviceProfilingError(ComplianceError.DeviceProfilingGeneral)
      isDeviceAppProfilingRunning.value = false
    }
  }

  const resetComplianceIds = (type: ResetComplianceIdType) => {
    if (type === RESET_COMPLIANCE_IDS_TYPES.None) return

    if (RESET_DEVICE_COMPLIANCE.includes(type)) {
      complianceIds.value.deviceProfiling = {}
    }

    if (!isNativeApp.value && RESET_GEO_CHECK.includes(type)) {
      complianceIds.value.geoCheck = {}
    }
  }

  const resetError = () => {
    error.value = null
  }

  const setDeviceProfilingError = (
    _error: ComplianceError = ComplianceError.DeviceProfilingGeneral,
  ) => {
    stopCompliance()
    resetComplianceIds(RESET_COMPLIANCE_IDS_TYPES.DeviceProfiling)
    error.value = _error
  }

  const setDeviceProfilingResponse = (sessionId: string) => {
    complianceIds.value.deviceProfiling.current = sessionId
    complianceIds.value.deviceProfiling.lastUpdated = new Date()
    if (isCompliant.value) error.value = null
  }

  const setGeoCheckError = (_error: ComplianceError = ComplianceError.GeoCheckGeneral) => {
    stopCompliance()
    resetComplianceIds(RESET_COMPLIANCE_IDS_TYPES.GeoCheck)
    error.value = _error
  }

  const setGeoCheckResponse = (result: LocationSmartResponse) => {
    complianceIds.value.geoCheck.current = result.sessionId
    complianceIds.value.geoCheck.lastUpdated = new Date()
    ipAddress.value = result.locationData.submitterIpAddress
    if (isCompliant.value) error.value = null
  }
  const startIntervals = () => {
    if (!isNativeApp.value && !intervals.value.geoCheckInterval) {
      intervals.value.geoCheckInterval = setInterval(() => geoCheck(), geoCheckConfig.interval)
    }
    const { app: appInterval, web: webInterval } = deviceProfilingConfig.intervals
    const interval = isNativeApp.value ? appInterval : webInterval
    if (!intervals.value.deviceProfilingInterval) {
      intervals.value.deviceProfilingInterval = setInterval(
        isNativeApp.value ? profileDeviceApp : profileDevice,
        interval,
      )
    }
  }
  const stopIntervals = () => {
    if (!isNativeApp.value && intervals.value.geoCheckInterval) {
      clearInterval(intervals.value.geoCheckInterval)
      intervals.value.geoCheckInterval = null
    }

    if (intervals.value.deviceProfilingInterval) {
      clearInterval(intervals.value.deviceProfilingInterval)
      intervals.value.deviceProfilingInterval = null
    }
  }

  const stopCompliance = (resetType: ResetComplianceIdType = RESET_COMPLIANCE_IDS_TYPES.All) => {
    if (cleanupVisibilityHandler.value) cleanupVisibilityHandler.value()
    stopIntervals()
    clearAllTimeouts()
    resetComplianceIds(resetType)
  }

  const startCompliance = async () => {
    if (import.meta.server) return

    if (!isMounted.value && env !== 'prod') {
      throw new Error('Component not mounted yet! Only call startCompliance if mounted.')
    }

    startIntervals()
    if (isNativeApp.value) {
      await profileDeviceApp()
    } else {
      injectGeoCheck()
      injectDeviceProfiling()
    }
    cleanupVisibilityHandler.value = useEventListener(
      // @ts-expect-error: useEventListener type does not include visibilitychange
      'visibilitychange',
      visibilityHandler,
    )
  }

  const visibilityHandler = () => {
    if (document.visibilityState === 'hidden') {
      stopIntervals()
      clearAllTimeouts()
    } else {
      checkComplianceIds()
      if (!isNativeApp.value) geoCheck()

      if (isNativeApp.value) profileDeviceApp()
      else profileDevice()

      startIntervals()
    }
  }

  const isCompliant = computed(
    () => (isNativeApp.value || !!html5SessionId.value) && !!profilingSessionId.value,
  )

  onBeforeUnmount(() => {
    try {
      stopCompliance()
    } catch {
      // Do nothing
    }
  })

  onMounted(() => (isMounted.value = true))

  const logComplianceError = (message: string, messageContext?: LooseObject) => {
    log('error', {
      appendPlayerInfo: true,
      component: 'compliance',
      message,
      ...(messageContext && { messageContext }),
    })
  }

  const logGeoCheckSuccess = useDebounceFn(() => {
    log('info', {
      appendPlayerInfo: true,
      component: 'compliance',
      message: 'Geo check succeeded',
      messageContext: { isNativeApp: isNativeApp.value },
    })
  }, 500)

  return {
    canCheckGeolocation,
    complianceIds: readonly(complianceIds.value),
    error,
    html5SessionId,
    ipAddress,
    isComplianceLoading,
    isCompliant,
    profilingSessionId,
    resetError,
    startCompliance,
    stopCompliance,
    submitterIPAddress,
  }
}
